(() => {
  if (customElements.get('myrevora-viewport-video')) return;

  const players = new Set();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const videoSelector = 'myrevora-video-carousel video, myrevora-how-it-works video';
  let observer;
  let lifecycle;
  let frame;
  let active;
  let soundMuted = true;
  const prepared = new WeakSet();
  const preloadTargets = new Map();
  const nearby = new Set();
  const connection = navigator.connection;
  let preloadObserver;
  let preloadReady = false;
  let idlePreparation;

  const mayPreload = () => preloadReady && !document.hidden && !motion.matches &&
    !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);

  const prepare = (player) => {
    if (!mayPreload() || !player?.isConnected || prepared.has(player.video)) return;
    prepared.add(player.video);
    player.video.preload = 'auto';
    player.video.setAttribute('loading', 'eager');
  };

  const prepareNext = (player) => {
    if (!mayPreload() || player !== active || player.ratio < 0.55 || player.video.paused) return;
    const track = player.closest('[data-video-track]');
    if (!track) return;
    const sequence = [...players].filter((item) => item.closest('[data-video-track]') === track);
    sequence.sort((a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
    prepare(sequence[sequence.indexOf(player) + 1]);
  };

  const prepareNearby = () => {
    if (!mayPreload()) return;
    nearby.forEach((target) => {
      const group = preloadTargets.get(target);
      if (!group?.size) return;
      if (!target.matches('[data-video-track]')) {
        prepare([...group][0]);
        return;
      }
      const bounds = target.getBoundingClientRect();
      let candidate;
      let best = -1;
      group.forEach((player) => {
        const rect = player.getBoundingClientRect();
        const visible = Math.max(0, Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left));
        const fraction = rect.width ? visible / rect.width : 0;
        if (fraction > best) { best = fraction; candidate = player; }
      });
      prepare(active && group.has(active) ? active : candidate);
    });
    if (active) prepareNext(active);
  };

  const setSoundMuted = (muted) => {
    soundMuted = muted;
    players.forEach((player) => {
      player.video.muted = soundMuted;
      player.sync();
    });
  };

  const pauseOthers = (video) => {
    players.forEach((player) => {
      if (player.video !== video) player.stop();
    });
    document.querySelectorAll(videoSelector).forEach((other) => {
      if (other !== video) other.pause();
    });
  };

  const select = () => {
    frame = null;
    const candidates = [...players].filter((player) => player.ratio >= 0.55);
    candidates.sort((a, b) => b.ratio - a.ratio);
    let next = candidates[0];
    if (active && candidates.includes(active) && next.ratio - active.ratio < 0.08) next = active;
    if (document.hidden) next = undefined;
    if (next !== active) {
      if (active) {
        active.stop();
        active.manualPause = false;
        active.failed = false;
      }
      active = next;
    }
    players.forEach((player) => {
      if (player !== active) player.stop();
    });
    if (active && !motion.matches && !active.manualPause && !active.failed && active.video.paused && !active.pending) {
      active.start(false);
    }
  };

  const schedule = () => {
    if (frame == null) frame = requestAnimationFrame(select);
  };

  const connect = (player) => {
    if (!observer) {
      lifecycle = new AbortController();
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { entry.target.ratio = entry.isIntersecting ? entry.intersectionRatio : 0; });
        schedule();
      }, { threshold: Array.from({ length: 21 }, (_, i) => i / 20) });
      preloadObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) nearby.add(entry.target);
          else nearby.delete(entry.target);
        });
        prepareNearby();
      }, { rootMargin: '1200px 0px', threshold: 0 });
      const enablePreloading = () => {
        const ready = () => { preloadReady = true; prepareNearby(); };
        idlePreparation = window.requestIdleCallback
          ? window.requestIdleCallback(ready, { timeout: 1500 })
          : setTimeout(ready, 200);
      };
      if (document.readyState === 'complete') enablePreloading();
      else window.addEventListener('load', enablePreloading, { once: true, signal: lifecycle.signal });
      connection?.addEventListener?.('change', prepareNearby, { signal: lifecycle.signal });
      document.addEventListener('visibilitychange', () => { schedule(); prepareNearby(); }, { signal: lifecycle.signal });
      motion.addEventListener('change', () => {
        if (motion.matches) players.forEach((item) => { if (!item.manualPlayback) item.stop(); });
        schedule();
        prepareNearby();
      }, { signal: lifecycle.signal });
      document.addEventListener('play', (event) => {
        if (!event.target.matches?.(videoSelector)) return;
        pauseOthers(event.target);
      }, { capture: true, signal: lifecycle.signal });
    }
    players.add(player);
    observer.observe(player);
    const target = player.closest('[data-video-track]') || player;
    if (!preloadTargets.has(target)) {
      preloadTargets.set(target, new Set());
      preloadObserver.observe(target);
    }
    preloadTargets.get(target).add(player);
    prepareNearby();
  };

  const disconnect = (player) => {
    observer?.unobserve(player);
    players.delete(player);
    const target = player.closest('[data-video-track]') || player;
    preloadTargets.forEach((group, observed) => {
      if (observed !== target && !group.has(player)) return;
      group.delete(player);
      if (!group.size) {
        preloadObserver?.unobserve(observed);
        preloadTargets.delete(observed);
        nearby.delete(observed);
      }
    });
    if (active === player) active = undefined;
    if (players.size) {
      schedule();
    } else {
      observer?.disconnect();
      observer = undefined;
      preloadObserver?.disconnect();
      preloadObserver = undefined;
      preloadTargets.clear();
      nearby.clear();
      if (window.cancelIdleCallback) window.cancelIdleCallback(idlePreparation);
      else clearTimeout(idlePreparation);
      preloadReady = false;
      lifecycle?.abort();
      cancelAnimationFrame(frame);
      frame = null;
    }
  };

  customElements.define('myrevora-viewport-video', class extends HTMLElement {
    connectedCallback() {
      this.setupFrame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      if (this.events || !this.isConnected) return;
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-video-toggle], [data-how-toggle]');
      this.sound = this.querySelector('[data-video-sound]');
      if (!this.video || !this.toggle || !this.sound) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.ratio = 0;
      this.manualPause = false;
      this.failed = false;
      this.pending = false;
      this.attempt = 0;
      this.video.muted = soundMuted;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.loop = true;
      const track = this.closest('myrevora-video-carousel')?.querySelector('[data-video-track]');
      [this.toggle, this.sound].forEach((button) => {
        let gesture;
        button.addEventListener('pointerdown', (event) => {
          gesture = { x: event.clientX, y: event.clientY, scroll: track?.scrollLeft || 0, moved: false };
        }, { passive: true, signal });
        button.addEventListener('pointermove', (event) => {
          if (gesture && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 8) gesture.moved = true;
        }, { passive: true, signal });
        button.addEventListener('pointercancel', () => { if (gesture) gesture.moved = true; }, { passive: true, signal });
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          const swiped = event.detail !== 0 && gesture && (gesture.moved || Math.abs((track?.scrollLeft || 0) - gesture.scroll) > 8);
          gesture = undefined;
          if (swiped) return;
          if (button === this.sound) {
            setSoundMuted(!soundMuted);
            return;
          }
          if (!this.video.paused || this.pending) {
            this.manualPause = true;
            this.stop();
          } else {
            this.manualPause = false;
            this.failed = false;
            if (active && active !== this) {
              active.manualPause = false;
              active.failed = false;
            }
            active = this;
            this.start(true);
          }
        }, { signal });
      });
      ['play', 'pause', 'ended', 'volumechange', 'error', 'emptied'].forEach((type) => {
        this.video.addEventListener(type, () => this.sync(), { signal });
      });
      this.video.addEventListener('playing', () => {
        prepared.add(this.video);
        prepareNext(this);
      }, { signal });
      this.sync();
      connect(this);
    }

    start(manual) {
      pauseOthers(this.video);
      this.manualPlayback = manual;
      this.video.muted = soundMuted;
      const attempt = ++this.attempt;
      this.pending = true;
      const finish = () => {
        if (attempt !== this.attempt) return;
        this.pending = false;
        this.sync();
      };
      try {
        Promise.resolve(this.video.play()).then(finish, () => {
          if (attempt !== this.attempt) return;
          this.failed = true;
          finish();
        });
      } catch (_) {
        this.failed = true;
        finish();
      }
    }

    stop() {
      this.attempt = (this.attempt || 0) + 1;
      this.pending = false;
      this.video?.pause();
      if (this.toggle) this.sync();
    }

    sync() {
      const playing = !this.video.paused && !this.video.ended;
      this.toggleAttribute('data-playing', playing);
      this.toggleAttribute('data-started', this.video.currentTime > 0 || playing);
      this.toggleAttribute('data-muted', this.video.muted);
      const action = playing ? 'השהה סרטון' : this.video.currentTime > 0 ? 'המשך סרטון' : 'הפעל סרטון';
      this.toggle.setAttribute('aria-label', `${action}: ${this.toggle.dataset.videoLabel}`);
      this.sound.setAttribute('aria-label', `${this.video.muted ? 'הפעל צליל' : 'השתק צליל'}: ${this.toggle.dataset.videoLabel}`);
      this.sound.setAttribute('aria-pressed', String(!this.video.muted));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.setupFrame);
      if (!this.events) return;
      this.stop();
      this.events.abort();
      this.events = undefined;
      disconnect(this);
    }
  });
})();
